import React, { useState, useEffect } from "react";
import { Button, FormControl, FormLabel, Input, Select, VStack, HStack, Box } from "@chakra-ui/react";
import { BenchmarkType } from "build-ts/enums";
import { DatePeriodEnum } from "build-ts/relative_date";
import showToast from "hooks/useCustomToast";

interface BenchmarkCreationFormProps {
  onClose: () => void;
  onCreateBenchmark: (name: string, type: BenchmarkType, granularity?: DatePeriodEnum) => void;
}

const BenchmarkCreationForm: React.FC<BenchmarkCreationFormProps> = ({ onClose, onCreateBenchmark }) => {
  const [benchmarkName, setBenchmarkName] = useState<string>("");
  const [benchmarkType, setBenchmarkType] = useState<BenchmarkType>(BenchmarkType.TARGET_BENCHMARK);
  const [granularity, setGranularity] = useState<DatePeriodEnum>(DatePeriodEnum.DAY);

  useEffect(() => {
    // Reset values when component mounts
    setBenchmarkName("");
    setBenchmarkType(BenchmarkType.TARGET_BENCHMARK);
    setGranularity(DatePeriodEnum.DAY);
  }, []);

  const handleSubmit = () => {
    if (!benchmarkName.trim()) {
      showToast("Benchmark name is required", "", "error");
      return;
    }

    // Only pass granularity for TARGET_BENCHMARK
    const selectedGranularity = benchmarkType === BenchmarkType.TARGET_BENCHMARK ? granularity : undefined;
    onCreateBenchmark(benchmarkName, benchmarkType, selectedGranularity);
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setBenchmarkName("");
    setBenchmarkType(BenchmarkType.TARGET_BENCHMARK);
    setGranularity(DatePeriodEnum.DAY);
  };

  return (
    <Box p={4}>
      <VStack spacing={4} align="stretch">
        <FormControl isRequired>
          <FormLabel>Benchmark Name</FormLabel>
          <Input
            value={benchmarkName}
            onChange={e => setBenchmarkName(e.target.value)}
            placeholder="Enter benchmark name"
          />
        </FormControl>

        <FormControl>
          <FormLabel>Benchmark Type</FormLabel>
          <Select value={benchmarkType} onChange={e => setBenchmarkType(Number(e.target.value) as BenchmarkType)}>
            <option value={BenchmarkType.VERSUS_BENCHMARK}>Versus Benchmark</option>
            <option value={BenchmarkType.TARGET_BENCHMARK}>Target Benchmark</option>
          </Select>
        </FormControl>

        {/* Show granularity dropdown only for TARGET_BENCHMARK */}
        {benchmarkType === BenchmarkType.TARGET_BENCHMARK && (
          <FormControl>
            <FormLabel>Granularity</FormLabel>
            <Select value={granularity} onChange={e => setGranularity(Number(e.target.value) as DatePeriodEnum)}>
              <option value={DatePeriodEnum.DAY}>Day</option>
              <option value={DatePeriodEnum.WEEK}>Week</option>
              <option value={DatePeriodEnum.MONTH}>Month</option>
              <option value={DatePeriodEnum.QUARTER}>Quarter</option>
              <option value={DatePeriodEnum.YEAR}>Year</option>
            </Select>
          </FormControl>
        )}

        <HStack spacing={3} pt={4}>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="purple" onClick={handleSubmit} isDisabled={!benchmarkName.trim()}>
            Create
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
};

export default BenchmarkCreationForm;
